package co.maytes.standupbot.dashboard.features.standupruns

import co.maytes.standupbot.shared.dto.standuprun.QuietCauseDto
import co.maytes.standupbot.shared.dto.standuprun.UserSectionDto

object QuietRunRules {
    const val MAX_ROWS = 50

    enum class Fix { OPEN_ROSTER, RERUN }

    data class Row(
        val userId: String,
        val name: String,
        val cause: QuietCauseDto,
        val label: String,
        val sentence: String,
        val fix: Fix?,
    )

    private val LISTED = setOf(
        QuietCauseDto.NOT_LINKED,
        QuietCauseDto.PARTIALLY_LINKED,
        QuietCauseDto.SOURCE_FAILED,
        QuietCauseDto.NOTHING_TO_REPORT,
        QuietCauseDto.SKIPPED,
    )

    private val ORDER = listOf(
        QuietCauseDto.NOT_LINKED,
        QuietCauseDto.SOURCE_FAILED,
        QuietCauseDto.PARTIALLY_LINKED,
        QuietCauseDto.ON_LEAVE,
        QuietCauseDto.SKIPPED,
        QuietCauseDto.NOTHING_TO_REPORT,
    )

    fun rows(sections: List<UserSectionDto>): List<Row> =
        sections
            .filter { it.quietCause in LISTED }
            .sortedWith(compareBy({ ORDER.indexOf(it.quietCause) }, { it.displayName.lowercase() }))
            .map(::toRow)

    fun hasCauses(sections: List<UserSectionDto>): Boolean = sections.any { it.quietCause != null }

    fun needsAttention(sections: List<UserSectionDto>): Boolean =
        sections.any { it.quietCause == QuietCauseDto.NOT_LINKED || it.quietCause == QuietCauseDto.SOURCE_FAILED }

    fun everyoneUnlinked(sections: List<UserSectionDto>): Boolean {
        val quiet = sections.filter { it.quietCause != null }
        return quiet.isNotEmpty() && quiet.size == sections.size && quiet.all { it.quietCause == QuietCauseDto.NOT_LINKED }
    }

    fun breakdown(sections: List<UserSectionDto>): String? {
        val counts = sections.mapNotNull { it.quietCause }.groupingBy { it }.eachCount()
        if (counts.isEmpty()) return null
        return ORDER.filter { it in counts }.joinToString(", ") { "${counts.getValue(it)} ${phrase(it)}" }
    }

    fun label(cause: QuietCauseDto): String =
        when (cause) {
            QuietCauseDto.ON_LEAVE -> "On leave"
            QuietCauseDto.SKIPPED -> "Skipped"
            QuietCauseDto.SOURCE_FAILED -> "Source error"
            QuietCauseDto.NOT_LINKED -> "Not linked"
            QuietCauseDto.PARTIALLY_LINKED -> "Partly linked"
            QuietCauseDto.NOTHING_TO_REPORT -> "Nothing to report"
        }

    fun sentence(section: UserSectionDto): String {
        val missing = providers(section.quietMissing)
        val failed = providers(section.quietFailed)
        return when (section.quietCause) {
            QuietCauseDto.NOT_LINKED -> "Not linked to $missing, so their activity cannot be read."
            QuietCauseDto.PARTIALLY_LINKED -> "No $missing account linked, so $missing work is not included."
            QuietCauseDto.SOURCE_FAILED -> "$failed did not answer, so this line may be missing."
            QuietCauseDto.NOTHING_TO_REPORT ->
                "No Linear or GitHub activity in the window." + if (section.leaveUnchecked) " Leave check was unavailable." else ""
            QuietCauseDto.SKIPPED -> "Skipped this run."
            QuietCauseDto.ON_LEAVE -> "On leave."
            null -> ""
        }
    }

    fun fixFor(cause: QuietCauseDto): Fix? =
        when (cause) {
            QuietCauseDto.NOT_LINKED, QuietCauseDto.PARTIALLY_LINKED -> Fix.OPEN_ROSTER
            QuietCauseDto.SOURCE_FAILED -> Fix.RERUN
            else -> null
        }

    fun fixLabel(fix: Fix): String =
        when (fix) {
            Fix.OPEN_ROSTER -> "Open roster"
            Fix.RERUN -> "Run for this day"
        }

    private fun toRow(section: UserSectionDto): Row {
        val cause = checkNotNull(section.quietCause)
        return Row(
            userId = section.userId,
            name = section.displayName.ifBlank { section.slackHandle ?: "Someone" },
            cause = cause,
            label = label(cause),
            sentence = sentence(section),
            fix = fixFor(cause),
        )
    }

    private fun phrase(cause: QuietCauseDto): String =
        when (cause) {
            QuietCauseDto.NOT_LINKED -> "not linked"
            QuietCauseDto.SOURCE_FAILED -> "could not be read"
            QuietCauseDto.PARTIALLY_LINKED -> "partly linked"
            QuietCauseDto.ON_LEAVE -> "on leave"
            QuietCauseDto.SKIPPED -> "skipped"
            QuietCauseDto.NOTHING_TO_REPORT -> "nothing to report"
        }

    private fun providers(names: List<String>): String =
        names.ifEmpty { listOf("LINEAR") }.joinToString(" and ") { if (it == "GITHUB") "GitHub" else "Linear" }
}
